"use client";

import type { ComponentProps, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

interface CopyToClipboardButtonProps {
  /** Text placed on the clipboard. */
  value: string;
  /** Toast shown on success (localisable). */
  successMessage: string;
  /** Accessible name — the visible label is often shorter than the intent. */
  ariaLabel: string;
  /**
   * Leading icon. Passed in rather than derived, because each caller sizes and
   * colours its own icon (`mr-2 h-4 w-4` / `h-5 w-5 text-muted-foreground` /
   * `size-4 shrink-0`) and those class strings are part of the shipped design.
   */
  icon: ReactNode;
  /** Visible label. */
  children: ReactNode;
  /** Native hover hint, when the caller wants one. */
  title?: string;
  size?: ComponentProps<typeof Button>["size"];
  className?: string;
}

/**
 * Labelled copy-to-clipboard button — one icon + label `outline` Button that
 * writes `value` to the clipboard and toasts.
 *
 * ── WHY THIS IS SEPARATE FROM `components/CopyButton.tsx` ────────────────────
 * `CopyButton` is the compact ICON-ONLY affordance: a bare `<button>` with its
 * own 44×44 touch-target class chain and a Copy→Check swap on success. This one
 * is a shadcn `<Button variant="outline">` with a visible label and NO
 * confirmation swap. The two render different elements with different attributes
 * (`data-slot`/`data-variant`/`data-size` vs none), so folding them into one
 * component would mean a branch that renders two unrelated trees — and any
 * attempt to make one serve both changes the markup on a live page. They share
 * the part that is genuinely shared: `hooks/useCopyToClipboard.ts`.
 *
 * `variant` is fixed to `outline` on purpose: all three consumers use it, and a
 * prop nobody passes is speculative API surface.
 */
export function CopyToClipboardButton({
  value,
  successMessage,
  ariaLabel,
  icon,
  children,
  title,
  size = "default",
  className,
}: CopyToClipboardButtonProps) {
  const { copy } = useCopyToClipboard();

  async function handleCopy() {
    await copy(value, { success: successMessage });
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      className={className}
      onClick={handleCopy}
      aria-label={ariaLabel}
      title={title}
    >
      {icon}
      {children}
    </Button>
  );
}
